import React, { useState } from "react";
import { CheckCircle, Users, Heart, Star } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const benefits = [
  "Tüm etkinliklere öncelikli katılım hakkı",
  "Gençlik ve burs programlarından yararlanma",
  "Dernek tesislerinden ücretsiz yararlanma",
  "Meslek ağına dahil olma",
  "Aylık e-bülten ve duyurular",
  "Oy kullanma ve yönetime katılma hakkı",
];

const MembershipPage: React.FC = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    birthCity: "Trabzon",
    profession: "",
    reason: "",
    newsletter: true,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Telefon normalize: rakamları al, 0-prefix'li 10 hane (mobil ile aynı).
    const digits = form.phone.replace(/\D/g, "");
    const on = digits.startsWith("0") ? digits.slice(1) : digits;
    if (on.length !== 10) {
      setError("Geçerli bir telefon numarası girin (0 olmadan 10 hane).");
      return;
    }
    const telefon = "0" + on;

    setSubmitting(true);
    try {
      // Firestore 'uyelikTalepleri' — mobil/admin ile BİREBİR aynı şema.
      // birthCity ve newsletter Firestore'a YAZILMAZ (şemada yok).
      await addDoc(collection(db, "uyelikTalepleri"), {
        ad: form.firstName.trim(),
        soyad: form.lastName.trim(),
        email: form.email.trim(),
        telefon,
        ilce: form.city.trim(),
        meslek: form.profession.trim(),
        neden: form.reason.trim(),
        tarih: serverTimestamp(),
        durum: "bekliyor",
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Üyelik talebi gönderilemedi:", err);
      setError("Başvuru gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-page pt-20">
        <div className="text-center max-w-md mx-auto px-4">
          <CheckCircle size={64} className="text-brand-green mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-text-primary mb-3">
            Başvurunuz Alındı!
          </h2>
          <p className="text-text-muted leading-relaxed">
            Üyelik başvurunuz yönetim kurulumuz tarafından incelenecek ve en
            kısa sürede sizinle iletişime geçilecektir. Derneğimize
            hoş geldiniz!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-page pt-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0F172A] to-brand-bordo-dark py-16 text-white">
        <div className="w-full px-6 xl:px-16 text-center">
          <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-2">
            Destek ve Katılım
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Üyelik Başvurusu</h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Bu çatının altında sana da yer var. Trabzon'a gönül veren,
            dayanışmaya inanan herkesi bekliyoruz.
          </p>
        </div>
      </div>

      <div className="w-full px-6 xl:px-16 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Benefits */}
          <div className="lg:col-span-1 space-y-5">
            <div className="card">
              <h3 className="font-bold text-text-primary mb-4 flex items-center gap-2">
                <Star size={18} className="text-brand-gold" />
                Üyelik Avantajları
              </h3>
              <ul className="space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-text-muted">
                    <CheckCircle
                      size={15}
                      className="text-brand-green mt-0.5 shrink-0"
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card bg-brand-bordo text-white">
              <Heart size={20} className="mb-3 text-white/70" />
              <p className="text-sm leading-relaxed text-white/85 italic">
                "Hemşerilik hukuku; coğrafyadan değil, gönülden gelir."
              </p>
            </div>

            <div className="card">
              <Users size={18} className="text-brand-blue mb-2" />
              <div className="text-2xl font-bold text-text-primary">500+</div>
              <div className="text-text-muted text-sm">Aktif üyemiz var</div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 card">
            <h3 className="font-bold text-text-primary text-xl mb-6">
              Üyelik Başvuru Formu
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Ad *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={form.firstName}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="Adınız"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Soyad *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={form.lastName}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="Soyadınız"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    E-posta *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="ornek@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="0555 000 00 00"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Bursa'daki İlçe
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="Nilüfer, Osmangazi..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Meslek
                  </label>
                  <input
                    type="text"
                    name="profession"
                    value={form.profession}
                    onChange={handleChange}
                    className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white"
                    placeholder="Mesleğiniz"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Derneğe katılmak isteme nedeniniz
                </label>
                <textarea
                  name="reason"
                  rows={3}
                  value={form.reason}
                  onChange={handleChange}
                  className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-bordo bg-white resize-none"
                  placeholder="Kısaca anlatır mısınız?"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="newsletter"
                  checked={form.newsletter}
                  onChange={handleChange}
                  className="w-4 h-4 accent-brand-bordo"
                />
                <span className="text-sm text-text-muted">
                  Dernek duyurularından ve e-bültenlerinden haberdar olmak
                  istiyorum.
                </span>
              </label>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-center gap-2">
                  <i className="fa-solid fa-triangle-exclamation" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full justify-center flex items-center gap-2 text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Gönderiliyor..." : "Başvuruyu Gönder"}
              </button>

              <p className="text-xs text-text-soft text-center">
                Bilgileriniz yalnızca dernek üyelik işlemleri için kullanılacak
                ve üçüncü şahıslarla paylaşılmayacaktır.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
